// src/routes/index.tsx

import type {
  ReactNode,
} from 'react';

import {
  BrowserRouter as Router,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../app/store';

import ManagementLayout
  from '../layouts/management/ManagementLayout';

import RouteConfig, {
  type RouteItem,
  type UserRole,
} from './RouteConfig';


type RoleRouteProps = {
  roles:
  readonly UserRole[];
};


type GuestRouteProps = {
  children:
  ReactNode;
};


type RouteElementProps = {
  route:
  RouteItem;
};


const RouteElement = ({
  route,
}: RouteElementProps) => {
  const Component =
    route.component;

  return <Component />;
};


/**
 * 권한별 기본 홈
 */
export const getRoleHomePath = (
  role: UserRole,
): string => {
  switch (role) {
    case 'USER':
      return RouteConfig
        .User
        .Home
        .fullPath;

    case 'ADMIN':
      return RouteConfig
        .Management
        .AdminHome
        .fullPath;

    case 'SUPERADMIN':
      return RouteConfig
        .SuperAdmin
        .Home
        .fullPath;

    default:
      return RouteConfig
        .Public
        .Landing
        .fullPath;
  }
};


/**
 * 권한 Route
 */
const RoleRoute = ({
  roles,
}: RoleRouteProps) => {
  const location =
    useLocation();

  const {
    accessToken,
    user,
  } =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth,
    );


  if (!accessToken) {
    return (
      <Navigate
        to={
          RouteConfig
            .Auth
            .Login
            .fullPath
        }
        replace
        state={{
          from:
            location.pathname,
        }}
      />
    );
  }


  if (!user) {
    return (
      <main className="grid min-h-screen place-items-center bg-gray-50">

        <div className="text-center">

          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-gray-900"
            role="status"
            aria-label="사용자 권한 확인 중"
          />

          <p className="mt-3 text-sm text-gray-500">
            사용자 권한을 확인하고 있습니다.
          </p>

        </div>

      </main>
    );
  }


  const userRole =
    user.role as
    UserRole;


  if (
    !roles.includes(
      userRole,
    )
  ) {
    return (
      <Navigate
        to={
          getRoleHomePath(
            userRole,
          )
        }
        replace
      />
    );
  }


  return <Outlet />;
};


/**
 * Guest Route
 */
const GuestRoute = ({
  children,
}: GuestRouteProps) => {
  const {
    accessToken,
    user,
  } =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth,
    );


  if (
    accessToken &&
    user?.role
  ) {
    return (
      <Navigate
        to={
          getRoleHomePath(
            user.role as
            UserRole,
          )
        }
        replace
      />
    );
  }


  return (
    <>
      {children}
    </>
  );
};


/**
 * /management 접근 시
 *
 * ADMIN
 * → /management/home
 *
 * SUPERADMIN
 * → /superadmin/home
 */
const ManagementIndexRedirect =
  () => {
    const user =
      useSelector(
        (
          state:
            RootState,
        ) =>
          state.auth.user,
      );


    if (!user?.role) {
      return (
        <Navigate
          to={
            RouteConfig
              .Auth
              .Login
              .fullPath
          }
          replace
        />
      );
    }


    return (
      <Navigate
        to={
          getRoleHomePath(
            user.role as
            UserRole,
          )
        }
        replace
      />
    );
  };


const AppRouter = () => {
  return (
    <Router>

      <Routes>

        {/* =================================================
            PUBLIC
            ================================================= */}

        <Route
          path={
            RouteConfig
              .Public
              .Landing
              .path
          }
          element={
            <RouteElement
              route={
                RouteConfig
                  .Public
                  .Landing
              }
            />
          }
        />


        <Route
          path={
            RouteConfig
              .Public
              .WithdrawalGuide
              .path
          }
          element={
            <RouteElement
              route={
                RouteConfig
                  .Public
                  .WithdrawalGuide
              }
            />
          }
        />


        {/* =================================================
            AUTH
            ================================================= */}

        <Route
          path={
            RouteConfig
              .Auth
              .Login
              .path
          }
          element={
            <GuestRoute>

              <RouteElement
                route={
                  RouteConfig
                    .Auth
                    .Login
                }
              />

            </GuestRoute>
          }
        />


        <Route
          path={
            RouteConfig
              .Auth
              .Signup
              .path
          }
          element={
            <GuestRoute>

              <RouteElement
                route={
                  RouteConfig
                    .Auth
                    .Signup
                }
              />

            </GuestRoute>
          }
        />


        <Route
          path={
            RouteConfig
              .Auth
              .FindId
              .path
          }
          element={
            <GuestRoute>

              <RouteElement
                route={
                  RouteConfig
                    .Auth
                    .FindId
                }
              />

            </GuestRoute>
          }
        />

        <Route
          path={
            RouteConfig
              .Auth
              .ResetPassword
              .path
          }
          element={
            <GuestRoute>

              <RouteElement
                route={
                  RouteConfig
                    .Auth
                    .ResetPassword
                }
              />

            </GuestRoute>
          }
        />


        {/* =================================================
            COMMON AUTHENTICATED
            ================================================= */}

        <Route
          element={
            <RoleRoute
              roles={
                RouteConfig
                  .Common
                  .ComingSoon
                  .roles
              }
            />
          }
        >

          <Route
            path={
              RouteConfig
                .Common
                .ComingSoon
                .path
            }
            element={
              <RouteElement
                route={
                  RouteConfig
                    .Common
                    .ComingSoon
                }
              />
            }
          />

        </Route>


        {/* =================================================
            USER
            ================================================= */}

        <Route
          element={
            <RoleRoute
              roles={
                RouteConfig
                  .User
                  .Home
                  .roles
              }
            />
          }
        >

          <Route
            path={
              RouteConfig
                .User
                .Root
                .path
            }
          >

            <Route
              index
              element={
                <Navigate
                  to={
                    RouteConfig
                      .User
                      .Home
                      .path
                  }
                  replace
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Home
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Home
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .History
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .History
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Ranking
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Ranking
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Tournaments
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Tournaments
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Attendance
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Attendance
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Coupons
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Coupons
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .Profile
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .Profile
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .User
                  .ComingSoon
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .User
                      .ComingSoon
                  }
                />
              }
            />

          </Route>

        </Route>


        {/* =================================================
            MANAGEMENT
            ADMIN + SUPERADMIN 공통 영역
            ================================================= */}

        <Route
          element={
            <RoleRoute
              roles={
                RouteConfig
                  .Management
                  .Games
                  .roles
              }
            />
          }
        >

          <Route
            path={
              RouteConfig
                .Management
                .Root
                .path
            }
            element={
              <ManagementLayout />
            }
          >

            {/* /management */}
            <Route
              index
              element={
                <ManagementIndexRedirect />
              }
            />


            {/* =============================================
                ADMIN ONLY HOME
                ============================================= */}

            <Route
              element={
                <RoleRoute
                  roles={
                    RouteConfig
                      .Management
                      .AdminHome
                      .roles
                  }
                />
              }
            >

              <Route
                path={
                  RouteConfig
                    .Management
                    .AdminHome
                    .path
                }
                element={
                  <RouteElement
                    route={
                      RouteConfig
                        .Management
                        .AdminHome
                    }
                  />
                }
              />

            </Route>


            {/* =============================================
                GAME
                ADMIN + SUPERADMIN
                ============================================= */}

            <Route
              path={
                RouteConfig
                  .Management
                  .Games
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .Games
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .GameCreate
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .GameCreate
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .GameDetail
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .GameDetail
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .TemplateCreate
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .TemplateCreate
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .TemplateEdit
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .TemplateEdit
                  }
                />
              }
            />


            {/* =============================================
                MEMBER
                ADMIN + SUPERADMIN
                ============================================= */}

            <Route
              path={
                RouteConfig
                  .Management
                  .Members
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .Members
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .MemberDetail
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .MemberDetail
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .Management
                  .WaitingMembers
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .WaitingMembers
                  }
                />
              }
            />

            <Route
              path={
                RouteConfig
                  .Management
                  .Coupons
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .Coupons
                  }
                />
              }
            />




            {/* =============================================
                ADMIN ONLY SETTING
                ============================================= */}

            <Route
              element={
                <RoleRoute
                  roles={
                    RouteConfig
                      .Management
                      .Setting
                      .roles
                  }
                />
              }
            >

              <Route
                path={
                  RouteConfig
                    .Management
                    .Setting
                    .path
                }
                element={
                  <RouteElement
                    route={
                      RouteConfig
                        .Management
                        .Setting
                    }
                  />
                }
              />

            </Route>


            <Route
              path={
                RouteConfig
                  .Management
                  .ComingSoon
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .Management
                      .ComingSoon
                  }
                />
              }
            />

          </Route>


          {/* =============================================
              TV
              Sidebar / Layout 없음
              ============================================= */}

          <Route
            path={
              RouteConfig
                .Management
                .TV
                .fullPath
            }
            element={
              <RouteElement
                route={
                  RouteConfig
                    .Management
                    .TV
                }
              />
            }
          />

        </Route>


        {/* =================================================
            SUPERADMIN ONLY
            ================================================= */}

        <Route
          element={
            <RoleRoute
              roles={
                RouteConfig
                  .SuperAdmin
                  .Home
                  .roles
              }
            />
          }
        >

          <Route
            path={
              RouteConfig
                .SuperAdmin
                .Root
                .path
            }
            element={
              <ManagementLayout />
            }
          >

            <Route
              index
              element={
                <Navigate
                  to={
                    RouteConfig
                      .SuperAdmin
                      .Home
                      .path
                  }
                  replace
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .Home
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .Home
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .Stores
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .Stores
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .Tournaments
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .Tournaments
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .Admins
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .Admins
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .Posters
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .Posters
                  }
                />
              }
            />


            <Route
              path={
                RouteConfig
                  .SuperAdmin
                  .ComingSoon
                  .path
              }
              element={
                <RouteElement
                  route={
                    RouteConfig
                      .SuperAdmin
                      .ComingSoon
                  }
                />
              }
            />

          </Route>

        </Route>


        {/* =================================================
            404
            ================================================= */}

        <Route
          path="*"
          element={
            <Navigate
              to={
                RouteConfig
                  .Public
                  .Landing
                  .fullPath
              }
              replace
            />
          }
        />

      </Routes>

    </Router>
  );
};


export default AppRouter;